/**
 * Reveals a group of siblings one after another.
 *
 * Returns one ref for the whole group; each child binds it and sets its own
 * --reveal-index, so CSS does the staggering and the delay costs nothing at
 * runtime. Like useScrollReveal, this returns state rather than touching
 * classes directly — Vue replaces `class` wholesale when it re-renders.
 *
 * Usage:
 *   const grid = ref(null)
 *   const { revealed } = useStaggerReveal(grid)
 *   <div ref="grid">
 *     <article v-for="(item, i) in items" class="reveal"
 *              :class="{ 'is-revealed': revealed }"
 *              :style="{ '--reveal-index': i }" />
 *   </div>
 */
import { ref, onMounted, onBeforeUnmount, unref } from "vue";
import { prefersReducedMotion } from "./useScrollReveal";

let observer = null;
const callbacks = new WeakMap();

function getObserver() {
  if (observer || typeof window === "undefined") return observer;

  observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        observer.unobserve(entry.target);
        callbacks.get(entry.target)?.();
        callbacks.delete(entry.target);
      });
    },
    { threshold: 0.1, rootMargin: "0px 0px -6% 0px" }
  );

  return observer;
}

/**
 * @param {import('vue').Ref<HTMLElement>|HTMLElement} target the group wrapper
 * @returns {{ revealed: import('vue').Ref<boolean> }}
 */
export function useStaggerReveal(target) {
  const revealed = ref(false);
  let el = null;

  onMounted(() => {
    el = unref(target);
    if (!el) return;

    if (prefersReducedMotion() || !("IntersectionObserver" in window)) {
      revealed.value = true;
      return;
    }

    callbacks.set(el, () => {
      revealed.value = true;
    });
    getObserver()?.observe(el);
  });

  onBeforeUnmount(() => {
    if (el && observer) {
      observer.unobserve(el);
      callbacks.delete(el);
    }
  });

  return { revealed };
}
